import { PRODUCT_INDEX_EN } from "@/constants/algolia";

/**
 * Maps a standard field/facet name to the correct attribute name based on the index.
 * Different Algolia indexes use different field naming conventions.
 *
 * @param facetName - The standard field/facet name (e.g., "subject", "learning_type", "title")
 * @param options - Optional config with indexName
 * @returns The correct attribute name for the given index
 */
export function getFacetAttributeName(
  facetName: string,
  options?: { indexName?: string },
): string {
  const indexName = options?.indexName || (PRODUCT_INDEX_EN as string);
  const isProductIndex = indexName === "product";

  // Mapping from standard names to index-specific names
  const facetMap: Record<string, { product: string; other: string }> = {
    // Subject facets
    subject: { product: "subject", other: "attributes" },
    attributes: { product: "subject", other: "attributes" },

    // Learning type / Product type (facet for filtering)
    learning_type: { product: "learning_type", other: "productType" },
    productType: { product: "learning_type", other: "productType" },

    // Skills
    "skills.skill": { product: "skills.skill", other: "skillsData.skill" },
    "skillsData.skill": { product: "skills.skill", other: "skillsData.skill" },

    // AI Languages - Transcription
    "ai_languages.transcription_languages": {
      product: "ai_languages.transcription_languages",
      other: "aiLanguages.transcriptionLanguages",
    },
    "aiLanguages.transcriptionLanguages": {
      product: "ai_languages.transcription_languages",
      other: "aiLanguages.transcriptionLanguages",
    },

    // AI Languages - Translation
    "ai_languages.translation_languages": {
      product: "ai_languages.translation_languages",
      other: "aiLanguages.translationLanguages",
    },
    "aiLanguages.translationLanguages": {
      product: "ai_languages.translation_languages",
      other: "aiLanguages.translationLanguages",
    },

    // Data fields - Product information
    title: { product: "title", other: "productName" },
    productName: { product: "title", other: "productName" },

    marketing_url: { product: "marketing_url", other: "productSlug" },
    productSlug: { product: "marketing_url", other: "productSlug" },

    partner_keys: { product: "partner_keys", other: "partnerKeys" },
    partnerKeys: { product: "partner_keys", other: "partnerKeys" },

    uuid: { product: "uuid", other: "productUuid" },
    productUuid: { product: "uuid", other: "productUuid" },

    program_type: { product: "program_type", other: "productType" },

    // Location restriction fields
    blocked_in: {
      product: "blocked_in",
      other: "locationRestrictions.blockedIn",
    },
    "locationRestrictions.blockedIn": {
      product: "blocked_in",
      other: "locationRestrictions.blockedIn",
    },

    allowed_in: {
      product: "allowed_in",
      other: "locationRestrictions.allowedIn",
    },
    "locationRestrictions.allowedIn": {
      product: "allowed_in",
      other: "locationRestrictions.allowedIn",
    },
  };

  // Check if we have a mapping for this field
  if (facetMap[facetName]) {
    return isProductIndex
      ? facetMap[facetName].product
      : facetMap[facetName].other;
  }

  // For fields that are the same across indexes (partner, level, language, availability, product),
  // just return the original name
  return facetName;
}
